// RTK Query slice for reservations
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./socketBaseQuery";

/** Normalize seating label */
const normalizeSeat = (val) => {
  if (val == null) return null;
  const s = String(val).trim();
  if (!s) return null;
  const l = s.toLowerCase();
  if (l.startsWith("terr")) return "Terras";
  if (l.startsWith("rest")) return "Restaurant";
  return s;
};

/** Transform raw reservation data from backend to normalized format */
const transformReservation = (reservation) => {
  const extraInfo =
    reservation.extraInfo && reservation.extraInfo.trim() !== ""
      ? reservation.extraInfo
      : "";
  const extraInfoAdmin =
    reservation.extraInfoAdmin && reservation.extraInfoAdmin.trim() !== ""
      ? reservation.extraInfoAdmin
      : "";
  const combinedExtra =
    [extraInfo, extraInfoAdmin].filter(Boolean).join("\n") || null;

  const isWaitlist =
    Boolean(reservation.isWaitlist) || Number(reservation.guests) === 0;

  const seatRaw =
    reservation?.zitplaats ?? reservation?.seat ?? reservation?.area ?? null;
  const seat = normalizeSeat(seatRaw);

  return {
    id: reservation._id,
    aantalGasten: reservation.guests,
    guestsWaiting: reservation.guestsWaiting || null,
    tijdstip: reservation.time,
    date: reservation.date,
    firstName: reservation.firstName,
    lastName: reservation.lastName,
    email: reservation.email,
    phone: reservation.phone,
    menu: reservation.menu,
    zitplaats: seat,
    seatClass: seat ? seat.toLowerCase() : null,
    personeel: reservation.personeel,
    extra: combinedExtra,
    extraInfo: reservation.extraInfo,
    extraInfoAdmin: reservation.extraInfoAdmin,
    createdAt: reservation.createdAt,
    status: reservation.status || "",
    tables: reservation.tables || [],
    giftcard: reservation.giftcard || "",
    eventCard: reservation.eventCard || "",
    reservationType: reservation.reservationType || "",
    isWaitlist,
    originalTime: reservation.originalTime || null,
    emailDelivered: reservation.emailDelivered,

    // ✅ Payment & Stripe fields
    paymentMethodId: reservation.paymentMethodId || null,
    stripePaymentMethodId: reservation.stripePaymentMethodId || null,
    stripeCustomerId: reservation.stripeCustomerId || null,

    // ✅ Hold fields
    holdPaymentIntentId: reservation.holdPaymentIntentId || null,
    enableInstantPayment: reservation.enableInstantPayment || false,
    holdStatus: reservation.holdStatus || null,
    holdAmountCents: reservation.holdAmountCents || null,
    currency: reservation.currency || "eur",
    holdError: reservation.holdError || null,
    scheduledRunAt: reservation.scheduledRunAt || null,

    // ✅ Verification fields
    hcaptchaVerified: reservation.hcaptchaVerified || false,
    hcaptchaTimestamp: reservation.hcaptchaTimestamp || null,
    hcaptchaHostname: reservation.hcaptchaHostname || null,
    hcaptchaCredit: reservation.hcaptchaCredit || false,

    // ✅ Meta fields
    restaurantId: reservation.restaurantId || null,
    ipAddress: reservation.ipAddress || null,
    ipInfo: reservation.ipInfo || {},
    storedNumber: reservation.storedNumber || null,
    termsAccepted: reservation.termsAccepted || false,

    // ✅ Children and babies
    children: reservation.children || 0,
    babies: reservation.babies || 0,

    // ✅ Giftcard flag
    isGiftcard: reservation.isGiftcard || reservation.reservationType === "giftcard_takeaway",

    // ✅ Duration for bowling restaurants
    duration: reservation.duration || null,
  };
};

export const reservationsApi = createApi({
  reducerPath: "reservationsApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  keepUnusedDataFor: 1800,
  tagTypes: ["Reservations"],
  endpoints: (builder) => ({
    getReservations: builder.query({
      query: () => ({
        url: "api/auth-reservations",
        method: "GET",
        headers: { "Cache-Control": "no-cache" },
      }),
      transformResponse: (response) => {
        const filteredData = (response || []).filter((reservation) => {
          return !(
            reservation.firstName === "Yeat" &&
            reservation.lastName === "Last Minute" &&
            reservation.reservationType === "yeat"
          );
        });

        const transformed = filteredData.map(transformReservation);

        // Helper to check if reservation is a real reservation (not waitlist, not giftcard)
        const isRealReservation = (r) => {
          const hasGuests = Number(r.aantalGasten) > 0;
          const isGiftcard = r.isGiftcard || r.reservationType === "giftcard_takeaway";
          return hasGuests && !isGiftcard;
        };

        // Calculate visit counts only for real reservations (exclude waitlist and giftcard)
        const emailVisitMap = new Map();
        const emailNoShowMap = new Map();
        const noShowNorm = (s) => (s || "").toString().trim().toLowerCase();
        const NOSHOW_STATUSES = new Set(["no_show", "no-show", "no show", "noshow", "niet gekomen"]);

        transformed.forEach((reservation) => {
          if (reservation.email && reservation.email.trim() && isRealReservation(reservation)) {
            const emailKey = reservation.email.toLowerCase();
            emailVisitMap.set(emailKey, (emailVisitMap.get(emailKey) || 0) + 1);
            if (NOSHOW_STATUSES.has(noShowNorm(reservation.status))) {
              emailNoShowMap.set(emailKey, (emailNoShowMap.get(emailKey) || 0) + 1);
            }
          }
        });

        // Add visitCount and noShowCount to each reservation
        return transformed.map((reservation) => {
          if (!isRealReservation(reservation) || !reservation.email || !reservation.email.trim()) {
            return { ...reservation, visitCount: 0, noShowCount: 0 };
          }

          const emailKey = reservation.email.toLowerCase();
          const visitCount = (emailVisitMap.get(emailKey) || 1) - 1;
          const noShowCount = emailNoShowMap.get(emailKey) || 0;

          return { ...reservation, visitCount, noShowCount };
        });
      },
      providesTags: ["Reservations"],
    }),

    // Get single reservation by ID
    getReservationById: builder.query({
      query: (id) => ({
        url: `api/auth-reservations/${id}`,
        method: "GET",
        headers: { "Cache-Control": "no-cache" },
      }),
      transformResponse: (response) => transformReservation(response),
      providesTags: (result, error, id) => [{ type: "Reservations", id }],
    }),

    updateReservation: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `api/reservations/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Reservations", id },
        "Reservations",
      ],
    }),

    deleteReservation: builder.mutation({
      query: (id) => ({
        url: `api/reservations/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Reservations"],
    }),

    createReservation: builder.mutation({
      query: (body) => ({
        url: "api/reservations",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Reservations"],
    }),

    placeAuthorizationHold: builder.mutation({
      query: ({ id, leadDays = 7 }) => ({
        url: `api/reservations/${id}/auto`,
        method: "POST",
        body: { leadDays },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Reservations", id },
        "Reservations",
      ],
    }),

    getHoldStatus: builder.query({
      query: (id) => ({
        url: `api/reservations/${id}/hold-status`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Reservations", id }],
    }),

    // ⭐ NEW: Capture hold - automatically refetch reservation after
    captureHold: builder.mutation({
      query: (holdId) => ({
        url: `api/reservations/hold/${holdId}/capture`,
        method: "POST",
      }),
      async onQueryStarted(holdId, { dispatch, queryFulfilled }) {
        console.log("📤 [captureHold] Starting capture for holdId:", holdId);
        try {
          await queryFulfilled;
          console.log("✅ [captureHold] Success - refetching reservation data");
          // Refetch all reservations to get updated hold status
          dispatch(reservationsApi.util.invalidateTags(["Reservations"]));
        } catch (err) {
          console.error("❌ [captureHold] Failed:", err);
        }
      },
      invalidatesTags: ["Reservations"],
    }),

    // ⭐ NEW: Cancel hold - automatically refetch reservation after
    cancelHold: builder.mutation({
      query: (holdId) => ({
        url: `api/reservations/hold/${holdId}/cancel`,
        method: "POST",
      }),
      async onQueryStarted(holdId, { dispatch, queryFulfilled }) {
        console.log("📤 [cancelHold] Starting cancel for holdId:", holdId);
        try {
          await queryFulfilled;
          console.log("✅ [cancelHold] Success - refetching reservation data");
          // Refetch all reservations to get updated hold status
          dispatch(reservationsApi.util.invalidateTags(["Reservations"]));
        } catch (err) {
          console.error("❌ [cancelHold] Failed:", err);
        }
      },
      invalidatesTags: ["Reservations"],
    }),

    // ⭐ NEW: Update reservation status - automatically refetch after
    updateReservationStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `api/auth-reservations/${id}`,
        method: "PUT",
        body: { status },
      }),
      async onQueryStarted({ id, status }, { dispatch, queryFulfilled }) {
        console.log("📤 [updateReservationStatus] Updating to:", status);
        try {
          await queryFulfilled;
          console.log("✅ [updateReservationStatus] Success - refetching reservation");
          // Invalidate specific reservation and all reservations list
          dispatch(
            reservationsApi.util.invalidateTags([
              { type: "Reservations", id },
              "Reservations",
            ])
          );
        } catch (err) {
          console.error("❌ [updateReservationStatus] Failed:", err);
        }
      },
    }),
  }),
});

export const {
  useGetReservationsQuery,
  useGetReservationByIdQuery,
  useUpdateReservationMutation,
  useDeleteReservationMutation,
  useCreateReservationMutation,
  usePlaceAuthorizationHoldMutation,
  useGetHoldStatusQuery,
  useCaptureHoldMutation,
  useCancelHoldMutation,
  useUpdateReservationStatusMutation,
} = reservationsApi;